
import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { Icons } from '../ui/Icons';

const PermissionErrorMessage: React.FC = () => {
    const [copyButtonText, setCopyButtonText] = useState('Copy Rules');

    // These are the correct and final rules for the application to function.
    // They are formatted for easy copying.
    const rulesJson = `{
  "rules": {
    ".read": "auth != null",
    ".write": "auth != null",
    "admins": {
      ".read": "true",
      ".write": "auth != null || !data.exists()"
    },
    "settings": {
      ".read": "true",
      ".write": "auth != null"
    },
    "users": {
      ".read": "true",
      ".write": "auth != null",
      ".indexOn": ["role", "phone", "email", "type"]
    },
    "scheduleData": {
      ".read": "true",
      ".write": "auth != null",
      "staffList": { ".indexOn": ["uid", "id"] }
    },
    "shuttleReservations": {
      ".read": "true",
      ".write": "auth != null",
      ".indexOn": ["date", "pickupLocationId"]
    },
    "parkingReservations": {
      ".read": "true",
      ".write": "auth != null",
      ".indexOn": ["startDate", "endDate", "status"]
    },
    "taskInstances": {
      ".read": "true",
      ".write": "auth != null",
      ".indexOn": ["date", "templateId", "status"]
    },
    "memos": {
      ".read": "true",
      ".write": "auth != null",
      ".indexOn": ["topicId", "categoryId", "published"]
    }
  }
}`;

    const handleCopy = () => {
        navigator.clipboard.writeText(rulesJson);
        toast.success('Rules copied to clipboard!');
        setCopyButtonText('Copied!');
        setTimeout(() => setCopyButtonText('Copy Rules'), 2000);
    };


  return (
    <div className="max-w-4xl mx-auto mt-10 p-6 bg-red-50 dark:bg-red-900/20 border-2 border-dashed border-red-200 dark:border-red-500/30 rounded-lg">
      <div className="flex items-start">
        <Icons.AlertTriangle className="h-8 w-8 text-red-500 mr-4 flex-shrink-0 mt-1" />
        <div>
          <h2 className="text-2xl font-bold text-red-800 dark:text-red-300">Action Required: Update Database Rules</h2>
          <p className="mt-2 text-red-700 dark:text-red-300">
            The application has detected a <strong>Permission Denied</strong> error. This means the app's code is trying to save data in a way that your current Firebase Security Rules do not allow.
          </p>
          <p className="mt-2 text-red-700 dark:text-red-300">
            To fix this permanently, please navigate to your Firebase project's <strong>Realtime Database &gt; Rules</strong> tab and replace the entire content of the editor with the rules below.
          </p>
          <div className="relative mt-4">
            <button
                onClick={handleCopy}
                className="absolute top-2 right-2 flex items-center gap-1.5 px-3 py-1 text-sm font-semibold text-gray-600 dark:text-gray-300 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-md hover:bg-gray-100 dark:hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all z-10"
            >
                {copyButtonText === 'Copy Rules' ? <Icons.Copy className="w-4 h-4" /> : <Icons.CalendarCheck className="w-4 h-4 text-green-500" />}
                {copyButtonText}
            </button>
            <pre className="p-4 bg-gray-800 dark:bg-slate-800/50 text-white rounded-md overflow-x-auto text-sm">
                <code className="language-json">
                  {rulesJson}
                </code>
            </pre>
          </div>
          <div className="mt-4 text-sm text-red-600 dark:text-red-400 space-y-2">
            <p><strong>Why these rules are necessary:</strong></p>
            <ul className="list-disc list-inside space-y-1">
                <li><strong><code>admins</code>:</strong> The updated rule `!data.exists()` creates a secure, one-time exception to allow the application to create the very first admin account. After that, authentication is required.</li>
                <li><strong><code>timeOffRequests</code>:</strong> This is now a top-level node. The rules allow both staff and admins to read and write requests, which is necessary for submitting, viewing cover requests, and managing approvals.</li>
                <li><strong><code>staffList</code>:</strong> This rule allows staff members to update their own profiles from the staff portal.</li>
                <li><strong>Secure & Efficient:</strong> This ruleset is optimized for how the application is designed to work, ensuring both security and functionality.</li>
            </ul>
             <p className="pt-2 font-semibold">After updating the rules in Firebase, please refresh this page. The error should be resolved.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PermissionErrorMessage;